"use client";

import { useAutoAnimate } from "@formkit/auto-animate/react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "~/components/v3/themed/Dialog";
import { If } from "../Conditional";
import { cn } from "~/utils/fontsConfig";
import React, { useEffect } from "react";
import { CustomizableLayout } from "~/types/CustomizableLayout";

type Props = {
    title?: string;
    description?: string;
    icon?: React.ReactNode;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    children?: React.ReactNode;
    header?: React.ReactNode;
    footer?: React.ReactNode;
    className?: string;
    overlayClassName?: string;
    customization?: CustomizableLayout;
};

const Modal = ({
    title,
    icon,
    description,
    open,
    onOpenChange,
    children,
    header,
    footer,
    className,
    overlayClassName,
    customization,
}: Props) => {
    // const [parent] = useAutoAnimate<HTMLDivElement>();

    // Google's autocomplete is technically rendered outside of the modal
    // this code prevents the modal from closing when the user clicks on the autocomplete
    const onInteractOutside = (e: any) => {
        const classes: Array<Array<string>> = [];
        e.composedPath().forEach((el: any) => {
            if (el.classList) {
                classes.push(Array.from(el.classList));
            }
        });
        // pac = classes naming for https://www.npmjs.com/package/react-google-autocomplete
        // primary use case is the "AddContact" modal
        if (classes.join("-").includes("pac-container")) {
            e.preventDefault();
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className={cn(
                    "max-h-[71vh] max-w-[90vw] overflow-y-auto border-v3-N600 bg-v3-N-600 px-0 pb-0 text-v3-N-300 sm:max-w-[450px]",
                    "text-rendering-optimizeLegibility",
                    "will-change-auto",
                    className,
                    customization?.mainCardClassName,
                )}
                style={{
                    fontSmooth: "always",
                    WebkitFontSmoothing: "antialiased",
                    MozOsxFontSmoothing: "grayscale",
                }}
                overlayClassName={overlayClassName}
                onInteractOutside={onInteractOutside}
            >
                {(title || description || icon || header) && (
                    <DialogHeader className="px-4">
                        {/* <If condition={!!icon}>
                            <div className="mb-2 flex h-[48px] w-[48px] items-center justify-center rounded-xl border-[1px] border-[#50565E] text-[24px] text-white">
                                {icon}
                            </div>
                        </If> */}
                        <If condition={!!title}>
                            <DialogTitle
                                className={cn(
                                    "text-rendering-optimizeLegibility mt-4 text-[20px] font-semibold text-white",
                                    customization?.labelClassName,
                                )}
                            >
                                {title}
                            </DialogTitle>
                        </If>
                        <If condition={!!header}>{header}</If>
                        <If condition={!!description}>
                            <DialogDescription
                                className={cn(
                                    "text-rendering-optimizeLegibility",
                                    customization?.descriptionClassName,
                                )}
                            >
                                {description
                                    ?.split("\n")
                                    .map((line, index) => (
                                        <p key={index}>{line}</p>
                                    ))}
                            </DialogDescription>
                        </If>
                    </DialogHeader>
                )}
                <div
                    // ref={parent}
                    className="text-rendering-optimizeLegibility h-full w-full px-4"
                >
                    {children}
                </div>
                {footer && (
                    <DialogFooter
                        className={cn(
                            "flex w-full flex-col bg-v3-N550 pb-4 md:flex-col",
                            customization?.secondaryCardClassName,
                        )}
                    >
                        <div className="mb-2 h-[1px] w-full bg-v3-N-500/60" />
                        <span className="px-4">{footer}</span>
                    </DialogFooter>
                )}
            </DialogContent>
        </Dialog>
    );
};

export const ModalFooter = ({ children }: { children: React.ReactNode }) => {
    return (
        <div className="flex flex-row items-center justify-end gap-4 p-4">
            {children}
        </div>
    );
};

export default Modal;
